import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useOffline } from '../../contexts/OfflineContext'
import Sidebar from './Sidebar'
import Header from './Header'
import OfflineIndicator from '../UI/OfflineIndicator'
import SessionWarningModal from '../UI/SessionWarningModal'
import CashRegisterWarningModal from '../UI/CashRegisterWarningModal'

const Layout: React.FC = () => {
  const { user, signOut, loading, sessionWarningTime, extendSession } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const { isOnline, pendingSyncCount } = useOffline()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, _setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    return saved !== null ? JSON.parse(saved) : true // Default to collapsed
  })
  const [showCashRegisterWarning, setShowCashRegisterWarning] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(!!document.fullscreenElement)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const onFullScreenChange = () => setIsFullScreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFullScreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullScreenChange)
  }, [])

  const handleSignOut = async () => {
    // Check if register is open when on POS page
    if (location.pathname === '/pos') {
      try {
        const { apiService } = await import('../../services/api')
        const response = await apiService.getCashRegisterStatus()
        if (response.success && ((response as any).isOpen || (response.data as any)?.isOpen)) {
          // Show modal to close register first
          setShowCashRegisterWarning(true)
          return
        }
      } catch (error) {
        console.error('Error checking register status:', error)
        // Continue with logout on error
      }
    }
    
    await signOut()
    navigate('/login')
  }

  const handleCloseRegister = () => {
    setShowCashRegisterWarning(false)
    // Navigate to POS to close register
    navigate('/pos')
  }

  const handleLogoutAnyway = async () => {
    setShowCashRegisterWarning(false)
    await signOut()
    navigate('/login')
  }

  // Handle navigation to login when user is not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login')
    }
  }, [loading, user, navigate])

  // Redirect to role start page when at Dashboard (/)
  useEffect(() => {
    if (!user || location.pathname !== '/') return
    const startPage = user.roles?.find((r: { start_page?: string | null }) => r.start_page)?.start_page
    if (startPage && startPage !== '/') {
      navigate(startPage, { replace: true })
    }
  }, [user, location.pathname, navigate])

  // Show loading while auth is being checked
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }
  
  // Don't render anything if user is not authenticated (navigation handled in useEffect)
  if (!user) {
    return null
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
        isCollapsed={sidebarCollapsed}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden when in full screen (e.g. KDS) */}
        {!isFullScreen && (
          <Header 
            user={user}
            onMenuClick={() => setSidebarOpen(true)}
            onThemeToggle={toggleTheme}
            isDark={isDark}
            onSignOut={handleSignOut}
          />
        )}

        {/* Offline Indicator */}
        {!isOnline && <OfflineIndicator />}

        {/* Main Content Area - flex min-h-0 for /waiter /pos /kds so Place Order / content fits without scroll */}
        <main className={`flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900 min-h-0 ${
          location.pathname === '/pos' || location.pathname === '/waiter' || location.pathname === '/kds' ? 'overflow-hidden flex flex-col' : ''
        }`}>
          {location.pathname === '/pos' || location.pathname === '/waiter' || location.pathname === '/kds' ? (
            <Outlet />
          ) : (
            <div className="container mx-auto px-6 py-8">
              <Outlet />
            </div>
          )}
        </main>

        {/* Sync Status */}
        {pendingSyncCount > 0 && (
          <div className="bg-yellow-100 dark:bg-yellow-900 border-t border-yellow-200 dark:border-yellow-700 px-6 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-yellow-800 dark:text-yellow-200">
                {pendingSyncCount} items pending sync
              </span>
              <button 
                onClick={() => window.location.reload()}
                className="text-sm text-yellow-800 dark:text-yellow-200 hover:underline"
              >
                Retry Sync
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Session Warning Modal */}
      <SessionWarningModal
        isOpen={sessionWarningTime !== null && sessionWarningTime > 0}
        timeRemaining={sessionWarningTime || 0}
        onExtend={extendSession}
        onLogout={async () => {
          await signOut()
          navigate('/login')
        }}
      />

      {/* Cash Register Warning Modal */}
      <CashRegisterWarningModal
        isOpen={showCashRegisterWarning}
        onCloseRegister={handleCloseRegister}
        onLogoutAnyway={handleLogoutAnyway}
        onCancel={() => setShowCashRegisterWarning(false)}
      />

    </div>
  )
}

export default Layout
